'use client'

import React from 'react'
import { useRouter, useSearchParams, usePathname } from 'next/navigation'
import clsx from 'clsx'
import styles from './styles.module.scss'

interface PaginationProps {
  currentPage: number
  totalPages: number
}

const WINDOW = 2

const range = (start: number, end: number): number[] =>
  [...Array(end - start + 1)].map((_, i) => start + i)

export default function Pagination({ currentPage, totalPages }: PaginationProps) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const pathname = usePathname()

  const visiblePages = range(
    Math.max(1, currentPage - WINDOW),
    Math.min(totalPages, currentPage + WINDOW),
  )

  const isFirstButtonVisible = visiblePages[0] !== 1
  const isLastButtonVisible = visiblePages[visiblePages.length - 1] !== totalPages

  const movePage = (page: number) => {
    window.scrollTo({ top: 0, behavior: 'smooth' })

    const params = new URLSearchParams(searchParams.toString())
    if (page === 1) {
      params.delete('page')
    } else {
      params.set('page', page.toString())
    }

    const queryString = params.toString()
    const newUrl = queryString ? `${pathname}?${queryString}` : pathname
    router.push(newUrl)
  }

  // リンク要素を生成するヘルパー関数
  const createLink = (page: number, children: React.ReactNode, rel?: string) => (
    <a className={styles.paginationLink} rel={rel} onClick={() => movePage(page)}>
      {children}
    </a>
  )

  // ページ要素を生成するヘルパー関数
  const createPageItem = (page: number) => (
    <span key={page} className={clsx(styles.page, { [styles.current]: page === currentPage })}>
      {page === currentPage ? (
        <span className={styles.paginationLink}>{page}</span>
      ) : (
        createLink(page, page)
      )}
    </span>
  )

  return (
    <nav className={styles.pagination}>
      {/* 最初と前のボタン */}
      {isFirstButtonVisible && (
        <>
          <span className={styles.first}>{createLink(1, '最初')}</span>
          <span className={styles.prev}>{createLink(currentPage - 1, '«', 'prev')}</span>
        </>
      )}

      {/* ページ番号 */}
      {visiblePages.map(createPageItem)}

      {/* 最後と次のボタン */}
      {isLastButtonVisible && (
        <>
          <span className={clsx(styles.page, styles.gap)}>…</span>
          <span className={styles.next}>{createLink(currentPage + 1, '»', 'next')}</span>
          <span className={styles.last}>{createLink(totalPages, '最後')}</span>
        </>
      )}
    </nav>
  )
}
